// ==================== TEMPLATE STATUS COMPONENT ====================

import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2, Clock, XCircle, AlertCircle, ArrowRight } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../../../components/ui/Card';
import { staggerItem } from '../../../utils/animations';
import type { TemplateStatusSummary } from '../../../services/customer-dashboard.service';

// ==================== TYPES ====================

interface TemplateStatusProps {
  templates: TemplateStatusSummary[];
  isLoading?: boolean;
}

// ==================== STATUS CONFIG ====================

const STATUS_CONFIG: Record<string, { icon: typeof CheckCircle2; color: string }> = {
  APPROVED: { icon: CheckCircle2, color: 'text-(--color-success)' },
  PENDING: { icon: Clock, color: 'text-(--color-warning)' },
  REJECTED: { icon: XCircle, color: 'text-(--color-error)' },
  DISABLED: { icon: AlertCircle, color: 'text-(--color-muted-gray)' },
};

// ==================== COMPONENT ====================

export const TemplateStatus = ({ templates, isLoading = false }: TemplateStatusProps) => {
  if (isLoading) {
    return (
      <motion.div variants={staggerItem}>
        <Card>
          <CardHeader className="border-b-0 pb-1 mb-0">
            <CardTitle className="text-base">Template Status</CardTitle>
          </CardHeader>
          <CardContent className="pt-2">
            <div className="space-y-2">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-10 bg-(--color-border-gray) rounded animate-pulse" />
              ))}
            </div>
          </CardContent>
        </Card>
      </motion.div>
    );
  }

  const getStatusIcon = (status: string) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.PENDING;
    const Icon = config.icon;
    return <Icon size={14} className={config.color} />;
  };

  return (
    <motion.div variants={staggerItem}>
      <Card>
        <CardHeader className="border-b-0 pb-1 mb-0">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Template Status</CardTitle>
            <Link 
              to="/app/templates" 
              className="flex items-center gap-1 text-xs text-(--color-signal-orange) hover:underline"
            >
              Manage
              <ArrowRight size={12} />
            </Link>
          </div>
        </CardHeader>
        <CardContent className="pt-2">
          {templates.length === 0 ? (
            <div className="py-4 text-center">
              <AlertCircle size={24} className="text-(--color-muted-gray) mx-auto mb-1" />
              <p className="text-xs text-(--color-muted-gray)">No templates yet</p>
              <Link to="/app/templates" className="text-xs text-(--color-signal-orange) hover:underline mt-1 inline-block">
                Create a template
              </Link>
            </div>
          ) : (
            <div className="space-y-1">
              {templates.map((template) => (
                <Link
                  key={template.id}
                  to={`/app/templates`}
                  className="flex items-center justify-between py-1.5 border-b border-(--color-border-gray) last:border-b-0 hover:bg-white/5 transition-colors -mx-1 px-1 rounded"
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium text-white truncate">{template.name}</p>
                    <p className="text-[10px] text-(--color-muted-gray)">{template.category}</p>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {getStatusIcon(template.status)}
                    <span className="text-[10px] text-(--color-soft-gray)">{template.status}</span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default TemplateStatus;
